import React from 'react';
import { Helmet } from 'react-helmet';
import { Navigate, type RouteObject } from 'react-router-dom';
import Login from '../views/Login';
import RouteGuard from './RouteGuard';
import Visualization from '../views/Visualization';
import NoMatch from '../views/NoMatch';
import Account from '../views/Account';
import AppLayout from '../components/layout/AppLayout';
import Dashboard from '../views/Dashboard';
import DatasourcesList from '../views/datasources/DatasourcesList';
import DatasourceEdit from '../views/datasources/DatasourceEdit';
import DatasourceDetail from '../views/datasources/DatasourceDetail';
import DatasetsList from '../views/datasets/DatasetsList';
import DatasetEdit from '../views/datasets/DatasetEdit';
import DatasetDetail from '../views/datasets/DatasetDetail';
import ModelsList from '../views/bda-models/ModelsList';
import ModelDetail from '../views/bda-models/ModelDetail';
import ServicesList from '../views/bda-services/ServicesList';
import ServiceEdit from '../views/bda-services/ServiceEdit';
import ServiceDetail from '../views/bda-services/ServiceDetail';
import BdaApplicationsList from '../views/bda-applications/BdaApplicationsList';
import BdaApplicationEdit from '../views/bda-applications/BdaApplicationEdit';
import BdaApplicationDetail from '../views/bda-applications/BdaApplicationDetail';
import ProjectsList from '../views/projects/ProjectsList';
import ProjectDetail from '../views/projects/ProjectDetail';
import ProjectEdit from '../views/projects/ProjectEdit';
import NotificationsList from '../views/notifications/NotificationsList';
import NotificationPreferences from '../views/notifications/NotificationPreferences';
import ControlPanelHome from '../views/control-panel/ControlPanelHome';
import UICustomization from '../views/control-panel/UICustomization';
import UsersList from '../views/control-panel/users-management/UsersList';
import UserEdit from '../views/control-panel/users-management/UserEdit';
import UserDetail from '../views/control-panel/users-management/UserDetail';
import OrganizationsList from '../views/control-panel/organizations-management/OrganizationsList';
import OrganizationMembers from '../views/control-panel/organizations-management/OrganizationMembers';

type Props = {
  title: string;
  element: React.ReactNode;
}

function Page({ title, element }: Props) {
  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      {element}
    </>
  )
}

export const APP_ROUTES: Array<RouteObject> = [
  {
    path: '/',
    element: <AppLayout />,
    children: [
      { index: true, element: <Navigate to={window.__env.AFTER_LOGIN_ROUTE || "/dashboard"} /> },
      {
        path: '/dashboard',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Dashboard" element={<Dashboard />} /> },
        ],
      },
      {
        path: '/account',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Dashboard" element={<Account />} /> },
        ],
      },
      {
        path: '/datasources',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Datasources List" element={<DatasourcesList />} /> },
          { path: 'create', element: <DatasourceEdit /> },
          { path: ':id', element: <DatasourceDetail /> },
          { path: ':id/edit', element: <DatasourceEdit /> },
        ]
      },
      {
        path: '/datasets',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Datasets List" element={<DatasetsList />} /> },
          { path: 'create', element: <DatasetEdit /> },
          { path: ':id', element: <DatasetDetail /> },
          { path: ':id/edit', element: <DatasetEdit /> },
        ]
      },
      {
        path: '/models',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Models List" element={<ModelsList />} /> },
          // { path: 'create', element: <ModelEdit /> },
          { path: ':id', element: <ModelDetail /> },
          // { path: ':id/edit', element: <ModelEdit /> },
        ]
      },
      {
        path: '/services',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Services List" element={<ServicesList />} /> },
          { path: 'create', element: <ServiceEdit /> },
          { path: ':id', element: <ServiceDetail /> },
          { path: ':id/edit', element: <ServiceEdit /> },
        ]
      },
      {
        path: '/bda-applications',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Applications List" element={<BdaApplicationsList />} /> },
          { path: 'create', element: <BdaApplicationEdit /> },
          { path: ':id', element: <BdaApplicationDetail /> },
          { path: ':id/edit', element: <BdaApplicationEdit /> },
        ]
      },
      {
        path: '/projects',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Projects List" element={<ProjectsList />} /> },
          { path: 'create', element: <ProjectEdit /> },
          { path: ':id', element: <ProjectDetail /> },
          { path: ':id/edit', element: <ProjectEdit /> },
        ]
      },
      {
        path: '/notifications',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Notifications" element={<NotificationsList />} /> },
          { path: 'preferences', element: <Page title="Notification Preferences" element={<NotificationPreferences />} /> },
        ]
      },
      {
        path: '/control-panel',
        element: <RouteGuard checkAdmin={true} />,
        children: [
          { index: true, element: <Page title="Control Panel" element={<ControlPanelHome />} /> },
          {
            path: 'users-management',
            children: [
              { index: true, element: <Page title="Control Panel - Users Management" element={<UsersList />} /> },
              { path: 'create', element: <UserEdit /> },
              { path: ':id', element: <UserDetail /> },
              { path: ':id/edit', element: <UserEdit /> },
            ]
          },
          {
            path: 'organizations-management',
            children: [
              { index: true, element: <Page title="Control Panel - Organizations Management" element={<OrganizationsList />} /> },
              { path: ':id/manage-members', element: <OrganizationMembers /> },
            ]
          },
          {
            path: 'ui-customization',
            children: [
              { index: true, element: <Page title="Control Panel - User Interface Customization" element={<UICustomization />} /> },
            ]
          },
        ]
      },
      {
        path: '/visualization',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Visualization" element={<Visualization />} /> },
        ]
      },
      { path: '*', element: <NoMatch /> },
      { path: '/login', element: <Page title="Login" element={<Login />} /> },
    ]
  },
]